import Form from '@components/Form'
import { displayName } from '@lib/GeospatialFile'
import { PlanAsset } from 'api-validations/locutus'
import { MeasureSystem } from 'api-validations/shared'
import { applicationTypesToUnits, controllableApplicationTypes } from 'api-validations/stitch'
import { type FC, useMemo } from 'react'
import {
  CircleSpinner,
  Icon,
  PanoramaSelectMenu,
  Popover,
  StatusChip,
  StatusChipColor
} from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { createVariants } from 'shared-ui/lib'
import { useLocalization } from 'shared-ui/localization'

import { toPlanAssetPreview } from '../../lib/planAssetPreview'
import {
  type CffTarget,
  DEFAULT_APPLICATION_TYPE,
  hasRates,
  type PlanFileReading,
  type PlanningField
} from '../../lib/planUpload/planEntry'
import {
  CreateFailure,
  CreateStatus,
  DestinationKind,
  type PlanUploadRow,
  ReadStatus
} from '../../lib/planUpload/planUploadReducer'
import { PlanAssetThumbnail } from '../PlanAssetThumbnail'
import { BinaryChoice } from './BinaryChoice'
import { CffPicker } from './CffPicker'
import { PlanAssetChip } from './PlanAssetChip'

const THUMBNAIL_PX = 40

const rowClasses = createVariants({
  // Below md, cells wrap instead of squeezing into the grid.
  base: 'flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 even:bg-gray-50 md:grid',
  variants: {
    destination: {
      [DestinationKind.Operation]:
        'md:grid-cols-[auto_auto_minmax(9rem,1.1fr)_minmax(10rem,1.9fr)_5rem_minmax(11rem,1.4fr)]',
      [DestinationKind.Field]:
        'md:grid-cols-[auto_auto_minmax(9rem,1.1fr)_minmax(10rem,1.9fr)_5rem_1.5rem]'
    },
    excluded: { true: 'opacity-50' }
  }
})

const Outcome: FC<{ row: PlanUploadRow }> = ({ row }) => {
  const { t } = useLocalization()
  const { outcome } = row

  if (outcome === undefined) return null
  if (outcome.status === CreateStatus.Waiting)
    return <Icon icon={SpriteMap.ClockIcon} className="size-5 text-gray-400" />
  if (outcome.status === CreateStatus.Creating) return <CircleSpinner size="size-4" />
  if (outcome.status === CreateStatus.Created)
    return <Icon icon={SpriteMap.CheckCircleIcon} className="size-5 text-green-600" />

  const message =
    outcome.failure === CreateFailure.Rejected && outcome.message !== ''
      ? outcome.message
      : t(($) => $.planning.upload.failure[outcome.failure])

  return (
    <Popover
      icon="warning"
      trigger={
        <button
          type="button"
          className="flex size-5 shrink-0 items-center justify-center"
          aria-label={message}
        >
          <Icon icon={SpriteMap.AlertIcon} className="size-5 text-red-600" />
        </button>
      }
    >
      <span className="pan-body-small max-w-64 rounded-md bg-black px-2 py-1 text-white shadow-sm">
        {message}
      </span>
    </Popover>
  )
}

/**
 * Flags shapefile prescriptions with no rates attached (missing .dbf).
 */
const RatelessWarning: FC = () => {
  const { t } = useLocalization()

  return (
    <Popover
      icon="warning"
      trigger={
        <button
          type="button"
          className="flex size-4 shrink-0 items-center justify-center"
          aria-label={t(($) => $.planning.upload.needsDbf)}
        >
          <Icon icon={SpriteMap.AlertIcon} className="size-4 text-red-600" />
        </button>
      }
    >
      <span className="pan-body-small max-w-64 rounded-md bg-black px-2 py-1 text-white shadow-sm">
        {t(($) => $.planning.upload.needsDbf)}
      </span>
    </Popover>
  )
}

/**
 * How an asset is read. A .2020 states each asset's type, so the row only shows it. A shapefile's
 * asset type and application type are the grower's to choose, and changing either re-reads the file.
 * Units show in the grower's own system, since crusher converts every rate to imperial.
 */
const Interpretation: FC<{
  row: PlanUploadRow
  importMeasureSystem: MeasureSystem
  disabled: boolean
  onReadAs: (reading: PlanFileReading) => void
}> = ({ row, importMeasureSystem, disabled, onReadAs }) => {
  const { t, localizeUnit, measureSystem } = useLocalization()
  const { entry } = row
  const assetTypeIsDeclared = !entry.assetTypeIsSettled

  if (!assetTypeIsDeclared) {
    const boundaryLabel = row.inverted
      ? t(($) => $.planning.upload.inverted)
      : t(($) => $.planning.boundary)
    const label =
      entry.assetType === PlanAsset.Prescription
        ? t(($) => $.agConcepts.applicationTypes[entry.applicationType])
        : boundaryLabel

    return (
      <>
        {row.inverted ? (
          <StatusChip text={label} color={StatusChipColor.Warning} />
        ) : (
          <PlanAssetChip asset={row.assetType} text={label} />
        )}
        {entry.assetType === PlanAsset.Prescription && (
          <span className="pan-detail text-gray-600">
            {localizeUnit(applicationTypesToUnits[entry.applicationType][measureSystem])}
          </span>
        )}
        {!hasRates(entry) && <RatelessWarning />}
      </>
    )
  }

  return (
    <>
      <BinaryChoice
        label={t(($) => $.labels.type)}
        value={row.assetType}
        disabled={disabled}
        options={[
          { value: PlanAsset.Boundary, label: t(($) => $.planning.boundary) },
          { value: PlanAsset.Prescription, label: t(($) => $.planning.prescription) }
        ]}
        onChange={(assetType) =>
          onReadAs(
            assetType === PlanAsset.Boundary
              ? { assetType: PlanAsset.Boundary }
              : {
                  assetType: PlanAsset.Prescription,
                  applicationType: DEFAULT_APPLICATION_TYPE,
                  measureSystem: importMeasureSystem
                }
          )
        }
      />

      {entry.assetType === PlanAsset.Prescription && (
        <div className="shrink-0">
          <PanoramaSelectMenu
            size="xs"
            options={controllableApplicationTypes.options}
            value={entry.applicationType}
            disabled={disabled}
            onChange={(next) =>
              next !== entry.applicationType &&
              onReadAs({
                assetType: PlanAsset.Prescription,
                applicationType: next,
                measureSystem: importMeasureSystem
              })
            }
            nameAccessor={(choice) => localizeUnit(applicationTypesToUnits[choice][measureSystem])}
          />
        </div>
      )}
      {!hasRates(entry) && <RatelessWarning />}
    </>
  )
}

export type RowPlacement =
  | {
      kind: DestinationKind.Operation
      fields: readonly PlanningField[]
      onTarget: (target: CffTarget) => void
    }
  | { kind: DestinationKind.Field }

type Props = {
  row: PlanUploadRow
  placement: RowPlacement
  importMeasureSystem: MeasureSystem
  locked: boolean
  onRename: (name: string) => void
  onReadAs: (reading: PlanFileReading) => void
  onToggle: (excluded: boolean) => void
}

/**
 * Represents an asset to be imported. Indicates what it is and where it's going.
 */
export const PlanEntryRow: FC<Props> = ({
  row,
  placement,
  importMeasureSystem,
  locked,
  onRename,
  onReadAs,
  onToggle
}) => {
  const { t, formatMetric } = useLocalization()
  const preview = useMemo(() => toPlanAssetPreview(row.entry), [row.entry])
  const isRereading = row.source.read.status === ReadStatus.Rereading

  return (
    <div className={rowClasses({ destination: placement.kind, excluded: row.excluded })}>
      <input
        type="checkbox"
        className="size-4 accent-green-600"
        checked={!row.excluded}
        disabled={locked}
        aria-label={row.name}
        onChange={(event) => onToggle(!event.target.checked)}
      />

      <PlanAssetThumbnail asset={row.entry} preview={preview} size={THUMBNAIL_PX} />

      <div className="flex min-w-0 flex-1 flex-col md:flex-none">
        <Form.Input
          className="mb-0 h-8 w-full"
          value={row.name}
          disabled={locked}
          aria-label={t(($) => $.labels.name)}
          onChange={(event) => onRename(event.target.value)}
        />
        <span className="pan-detail truncate text-gray-500">{displayName(row.source.file)}</span>
      </div>

      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <Interpretation
          row={row}
          importMeasureSystem={importMeasureSystem}
          disabled={locked || isRereading}
          onReadAs={onReadAs}
        />
      </div>

      <span className="pan-detail text-right text-gray-600">
        {preview === undefined ? '--' : formatMetric({ value: preview.acres, unit: 'ac' })}
      </span>

      <div className="flex min-w-0 items-center gap-2">
        {placement.kind === DestinationKind.Operation && (
          <CffPicker
            fields={placement.fields}
            target={row.target}
            disabled={locked}
            onChange={placement.onTarget}
          />
        )}

        {(isRereading || row.outcome !== undefined) && (
          <div className="flex w-6 shrink-0 justify-center">
            {isRereading ? <CircleSpinner size="size-4" /> : <Outcome row={row} />}
          </div>
        )}
      </div>
    </div>
  )
}

export const PlanEntryRowHeader: FC<{
  destination: DestinationKind
  allIncluded: boolean
  disabled: boolean
  onToggleAll: (excluded: boolean) => void
}> = ({ destination, allIncluded, disabled, onToggleAll }) => {
  const { t } = useLocalization()

  return (
    <div className={rowClasses({ destination, className: 'pan-label bg-gray-50' })}>
      <input
        type="checkbox"
        className="size-4 accent-green-600"
        checked={allIncluded}
        disabled={disabled}
        aria-label={t(($) => $.planning.upload.toggleAll)}
        onChange={(event) => onToggleAll(!event.target.checked)}
      />
      <span className="hidden w-10 md:block" />
      <span className="hidden md:block">{t(($) => $.labels.name)}</span>
      <span className="hidden md:block">{t(($) => $.labels.type)}</span>
      <span className="hidden text-right md:block">{t(($) => $.acres)}</span>
      {destination === DestinationKind.Operation && (
        <span className="hidden md:block">{t(($) => $.agConcepts.field, { count: 1 })}</span>
      )}
    </div>
  )
}
